import {DragDropProvider} from '@wandb/weave/common/containers/DragDropContainer';
import {PanelExportContextProvider} from '@wandb/weave/components/Panel2/PanelExportContext';
import React, {
  type FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Editor,
  Element,
  type Node,
  type NodeEntry,
  Range,
  Transforms,
} from 'slate';
import {
  ReactEditor,
  type RenderElementProps,
  type RenderLeafProps,
  Slate,
} from 'slate-react';

import {useReportContext} from '../../../state/reports/context';
import {type ReportViewRef} from '../../../state/reports/types';
import {type ReportWidthOption} from '../../../state/views/report/types';
import {type LoadableView} from '../../../state/views/types';
import {useAdminModeActive} from '../../../util/admin';
import {useScrollToURLHash} from '../../../util/document';
import {useIsGalleryReport} from '../../../util/gallery';
import {ReportDiscussion} from '../../ReportDiscussion/ReportDiscussion';
import ReportPageSubscribePrompt from '../../ReportPageSubscribePrompt';
import {useCompositionKeyboard} from '../CompositionKeyboardContext';
import {FocusedContext} from '../FocusedContext';
import {HoveringToolbar} from '../HoveringToolbar';
import {autoScrollOnKeyDown} from '../plugins/auto-scroll';
import {useCodeHighlighting} from '../plugins/code-blocks';
import {WBSlateDragDropContext} from '../plugins/drag-drop';
import {hardBreakOnKeyDown} from '../plugins/hard-break';
import {EditorWithLists} from '../plugins/lists';
import {markShortcutsOnKeyDown} from '../plugins/mark-shortcuts';
import SlashMenu from '../SlashMenu';
import {TypingContext} from '../TypingContext';
import {createWBSlateEditor} from './createWBSlateEditor';
import {type WBSlateElementType} from './types';
import {hasEditableTarget} from './utils';
import * as S from './WBSlate.styles';
import {WBSlateElement} from './WBSlateElement';
import {WBSlateLeaf} from './WBSlateLeaf';

export interface WBSlateProps {
  readOnly: boolean;
  widthMode: ReportWidthOption;
  value: WBSlateElementType[];
  viewRef: ReportViewRef | null;
  view: LoadableView | null;
  viewID: string;
  onChange(value: Node[]): void;
}

export const WBSlate: FC<WBSlateProps> = ({
  readOnly,
  widthMode,
  value,
  viewRef,
  view,
  viewID,
  onChange,
}) => {
  const admin = useAdminModeActive();
  const {disableComments, disablePersistentTopLine} = useReportContext();
  const editor = React.useMemo(
    // Due to security/usability concerns, we want replit embeds in reports to be usable only by W&B admins.
    () => createWBSlateEditor({enableReplit: admin, disablePersistentTopLine}),
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const initialFocused = ReactEditor.isFocused(editor);
  const [focused, setFocused] = useState(initialFocused);

  const [slashMenuOpen, setSlashMenuOpen] = React.useState(false);

  const parentViewId = view?.parentId ?? view?.id;

  const [dragItem, setDragItem] = React.useState<Node | undefined>(undefined);
  // This is true if any of the drag handle menus are open. Disables the popup on the other handles.
  const [dragHandleMenuOpen, setDragHandleMenuOpen] = React.useState(false);
  const dragContextValue = React.useMemo(
    () => ({
      parentViewId,
      dragItem,
      setDragItem,
      dragHandleMenuOpen,
      setDragHandleMenuOpen,
    }),
    [
      parentViewId,
      dragItem,
      setDragItem,
      dragHandleMenuOpen,
      setDragHandleMenuOpen,
    ]
  );

  const [typing, setTyping] = React.useState(true);
  const typingContextValue = React.useMemo(
    () => ({typing, setTyping}),
    [typing]
  );

  const {startComposition, endComposition} = useCompositionKeyboard();

  // HAX: DO NOT REMOVE. THE "CONVERT ALL MARKDOWN" BUTTON IS RELIANT ON THIS.
  // "sry not sry" - Axel
  (window as any).editor = editor;
  (window as any).Editor = Editor;
  (window as any).ReactEditor = ReactEditor;
  (window as any).Transforms = Transforms;
  (window as any).value = value;

  useScrollToURLHash(1000);

  const {isGalleryReport} = useIsGalleryReport(viewID);

  const renderElement = React.useCallback(
    (props: RenderElementProps) => {
      // check if the focus has shifted from/in slate
      // if so, rerender WBSlate to rerender child elements
      const editorIsFocused = ReactEditor.isFocused(editor);
      setFocused(editorIsFocused);

      return <WBSlateElement {...props} />;
    },
    [editor]
  );
  const renderLeaf = React.useCallback((props: RenderLeafProps) => {
    return <WBSlateLeaf {...props} />;
  }, []);

  const addWeavePanel = React.useCallback(
    (panel: any) => {
      const weavePanelNode: Node = {
        type: 'weave-panel',
        children: [{text: ''}],
        config: {
          exp: panel.node,
          panelId: panel.panelId,
          panelConfig: panel.config,
        },
      };
      Editor.insertNode(editor, weavePanelNode);
    },
    [editor]
  );

  const decorate = useCodeHighlighting(editor);

  const slateValue = useMemo(() => {
    // Slate throws an error if the value on the initial render is invalid
    // so we directly set the value on the editor in order
    // to be able to trigger normalization on the initial value before rendering
    editor.children = value;
    Editor.normalize(editor, {force: true});
    // We return the normalized internal value so that the rendering can take over from here
    return editor.children;
  }, [editor, value]);

  useEffect(() => {
    function keydownListener() {
      setTyping(true);
    }
    // Not a shortcut: tracks typing activity, not an inputSystem candidate
    // oxlint-disable-next-line wandb/no-ad-hoc-keydown-listener
    window.addEventListener('keydown', keydownListener);

    return () => {
      window.removeEventListener('keydown', keydownListener);
    };
  }, []);

  React.useLayoutEffect(() => {
    if (!readOnly) {
      ReactEditor.focus(editor);
      Transforms.select(editor, Editor.start(editor, []));
    }
  }, [editor, readOnly]);

  return (
    <S.SlateWrapper data-test="wbslate-report-content">
      <S.PrintReportHack />
      <Slate editor={editor} initialValue={slateValue} onChange={onChange}>
        <TypingContext.Provider value={typingContextValue}>
          {/* For letting child components know focus has changed */}
          <FocusedContext.Provider value={focused}>
            <SlashMenu
              open={slashMenuOpen}
              onClose={React.useCallback(() => setSlashMenuOpen(false), [])}
            />
            <HoveringToolbar readOnly={readOnly} />
            <PanelExportContextProvider addPanel={addWeavePanel}>
              {/* For reordering blocks */}
              <WBSlateDragDropContext.Provider value={dragContextValue}>
                {/* For panels in panel grids */}
                <DragDropProvider>
                  <S.StyledEditable
                    onDrop={e => {
                      // Do not use Slate's default drop behavior since we have our own drag and drop logic too
                      e.preventDefault();
                    }}
                    data-test="report-editable-content"
                    decorate={decorate}
                    readOnly={readOnly}
                    $widthMode={widthMode}
                    renderElement={renderElement}
                    renderLeaf={renderLeaf}
                    scrollSelectionIntoView={() => {}} // no-op to disable Slate's default scroll behavior when dragging an overflowed element
                    onPaste={(event: React.ClipboardEvent<HTMLDivElement>) => {
                      // this is slightly modified from slate-react's internal onPaste handler
                      // we need this because slate doesn't handle paste correctly on Safari
                      if (
                        hasEditableTarget(editor, event.target) &&
                        !readOnly
                      ) {
                        event.preventDefault();
                        try {
                          ReactEditor.insertData(editor, event.clipboardData);
                        } catch (err) {
                          console.log('Slate onPaste error: ', err);
                        }
                      }
                    }}
                    onCut={() => {
                      /** Ugly hack to fix cut on a single void node */
                      const {selection} = editor;
                      if (
                        selection &&
                        Range.isCollapsed(selection) &&
                        Editor.void(editor)
                      ) {
                        window.setTimeout(() => {
                          Editor.deleteFragment(editor);
                        });
                      }
                    }}
                    onKeyDown={e => {
                      if (
                        autoScrollOnKeyDown(editor, e) ||
                        markShortcutsOnKeyDown(editor, e) ||
                        hardBreakOnKeyDown(editor, e)
                      ) {
                        return;
                      }

                      if (e.key === '/') {
                        window.setTimeout(() => {
                          setSlashMenuOpen(true);
                        });
                      }

                      // Prevents newline. Necessary in Firefox.
                      if (e.key === 'Enter' && slashMenuOpen) {
                        e.preventDefault();
                      }

                      if (e.key === 'Tab') {
                        e.preventDefault();

                        if (EditorWithLists.onTab(editor, e)) {
                          return;
                        }

                        Transforms.insertText(editor, '\t');
                        return;
                      }

                      // Unfortunate hack because Slate doesn't
                      // detect backspaces on inline voids
                      // in Chrome.
                      if (e.key === 'Backspace') {
                        const inlineVoid = Editor.above(editor, {
                          match: (n: Node, path) =>
                            Element.isElement(n) &&
                            Editor.isVoid(editor, n) &&
                            Editor.isInline(editor, n),
                        });
                        if (inlineVoid != null) {
                          e.preventDefault();
                          Editor.deleteBackward(editor);
                          return;
                        }
                      }
                    }}
                    onMouseMove={() => {
                      setTyping(false);
                    }}
                    onCompositionStart={startComposition}
                    onCompositionEnd={endComposition}
                  />
                </DragDropProvider>
                <ReportPageSubscribePrompt viewID={viewID} />
                {!disableComments && (
                  <ReportDiscussion isViewingReport={readOnly} />
                )}
              </WBSlateDragDropContext.Provider>
            </PanelExportContextProvider>
          </FocusedContext.Provider>
        </TypingContext.Provider>
      </Slate>
    </S.SlateWrapper>
  );
};
